import { config } from "../config/environment";

// File validation helpers
export const validateFileSize = (file: File): boolean => {
  return file.size <= config.upload.maxFileSize;
};

export const validateFileType = (file: File): boolean => {
  return config.upload.allowedFileTypes.includes(file.type);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Pagination helpers
export const getPageSize = (requestedSize?: number): number => {
  if (!requestedSize) return config.pagination.defaultPageSize;
  return Math.min(requestedSize, config.pagination.maxPageSize);
};

// Logging helpers
export const log = (
  message: string,
  level: "debug" | "info" | "warn" | "error" = "info",
  data?: any
) => {
  if (!config.development.debug && level === "debug") return;

  const timestamp = new Date().toISOString();
  const logMessage = `[${timestamp}] [${level.toUpperCase()}] ${message}`;

  switch (level) {
    case "debug":
      console.debug(logMessage, data);
      break;
    case "info":
      console.info(logMessage, data);
      break;
    case "warn":
      console.warn(logMessage, data);
      break;
    case "error":
      console.error(logMessage, data);
      break;
  }
};

// Session management helpers
export const checkSessionTimeout = (): boolean => {
  const lastActivity = localStorage.getItem("lastActivity");
  if (!lastActivity) return false;

  const now = Date.now();
  const lastActivityTime = parseInt(lastActivity);
  const timeDiff = now - lastActivityTime;

  return timeDiff > config.auth.sessionTimeout;
};

export const updateLastActivity = (): void => {
  localStorage.setItem("lastActivity", Date.now().toString());
};

// API URL helpers
export const buildApiUrl = (endpoint: string): string => {
  const baseUrl = config.api.baseUrl.replace(/\/$/, "");
  const cleanEndpoint = endpoint.replace(/^\//, "");
  return `${baseUrl}/${cleanEndpoint}`;
};

// Error handling helpers
export const handleApiError = (error: any): string => {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const message = error.response.data?.message || error.response.statusText;

    log(`API Error ${status}: ${message}`, "error", error.response.data);

    switch (status) {
      case 401:
        return "Unauthorized. Please login again.";
      case 403:
        return "Access denied. You don't have permission to perform this action.";
      case 404:
        return "Resource not found.";
      case 422:
        return "Validation error. Please check your input.";
      case 500:
        return "Server error. Please try again later.";
      default:
        return message || "An unexpected error occurred.";
    }
  } else if (error.request) {
    // Network error
    log("Network error: No response received", "error", error.request);
    return "Network error. Please check your connection.";
  } else {
    // Other error
    log("Error: " + error.message, "error", error);
    return error.message || "An unexpected error occurred.";
  }
};
